import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { TrendBadge } from "@/components/base/Shared";
import Timeline from "./components/Timeline";
import HeatChart from "./components/HeatChart";
import { hotEvents } from "@/mocks/events";
import { formatHeat } from "@/utils/format";

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const event = hotEvents.find((item) => item.id === id);

  const related = useMemo(
    () => hotEvents.filter((item) => item.id !== id).slice(0, 3),
    [id],
  );

  const jsonLd = useMemo(() => {
    if (!event) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: event.title,
        description: event.summary,
        articleSection: event.category,
        datePublished: event.startDate.replace(" ", "T") + ":00+08:00",
        inLanguage: "zh-CN",
        keywords: ["网红黑曝", "网红黑曝发酵", "网红黑曝上热搜", "网红黑曝话题量", ...event.tags].join(","),
        author: { "@type": "Organization", name: "瓜链" },
        publisher: { "@type": "Organization", name: "瓜链" },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://gualian.example.com/" },
          { "@type": "ListItem", position: 2, name: "事件追踪库", item: "https://gualian.example.com/events" },
          {
            "@type": "ListItem",
            position: 3,
            name: event.title,
            item: `https://gualian.example.com/event/${event.id}`,
          },
        ],
      },
    ];
  }, [event]);

  if (!event) {
    return (
      <>
        <Seo
          title="事件未找到 - 瓜链传播追踪"
          description="你访问的事件可能已经归档或链接有误，可以回到事件追踪库查看当前正在发酵的网红黑曝事件。"
          keywords="网红黑曝事件,传播追踪库,事件归档"
          canonicalPath="/events"
        />
        <div className="min-h-screen bg-background-50">
          <SiteHeader />
          <main className="pt-32 pb-24 px-4 md:px-8">
            <div className="max-w-xl mx-auto text-center">
              <span className="mx-auto w-16 h-16 rounded-2xl bg-background-100 text-foreground-500 flex items-center justify-center">
                <i className="ri-file-unknow-line text-2xl" />
              </span>
              <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
                这个事件暂时没有记录
              </h1>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                可能链接有误，或者该事件的数据还在整理中。你可以回到事件追踪库，看看当前哪些话题正在发酵。
              </p>
              <Link
                to="/events"
                className="mt-7 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label whitespace-nowrap transition-colors"
              >
                返回事件追踪库
              </Link>
            </div>
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  const maxChannel = Math.max(...event.channels.map((c) => c.value));

  return (
    <>
      <Seo
        title={`${event.title} - 发酵时间线与热度走势 | 瓜链传播追踪`}
        description={`${event.summary.slice(0, 70)}。当前热度 ${formatHeat(event.heat)}，话题量 ${event.topicCount}万，观测窗口 ${event.duration}，附完整发酵时间线与扩散渠道占比。`}
        keywords={`网红黑曝,${event.category},网红黑曝发酵,网红黑曝上热搜,${event.tags.join(",")},网红黑曝话题量`}
        canonicalPath={`/event/${event.id}`}
        ogImage={event.cover}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background-50">
        <SiteHeader />

        <section className="bg-background-100 border-b border-background-200 pt-28 md:pt-32 pb-10 md:pb-12">
          <div className="w-full px-4 md:px-8">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "事件追踪库", to: "/events" },
                { label: event.title },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="px-2.5 py-1 rounded-md bg-secondary-50 text-secondary-800 text-xs font-label">
                {event.category}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-50 border border-background-300 text-xs font-label text-foreground-700">
                {event.plat}首爆
              </span>
              <TrendBadge trend={event.trend} rate={event.trendRate} />
            </div>
            <h1 className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground-950 leading-tight tracking-tight max-w-4xl">
              {event.title}
            </h1>
            <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {event.summary}
            </p>

            <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { k: "首爆时间", v: event.startDate, icon: "ri-map-pin-time-line" },
                { k: "峰值时间", v: event.peakTime, icon: "ri-line-chart-line" },
                { k: "观测窗口", v: event.duration, icon: "ri-timer-flash-line" },
                { k: "首发平台", v: event.plat, icon: "ri-broadcast-line" },
              ].map((item) => (
                <div
                  key={item.k}
                  className="rounded-xl bg-background-50 border border-background-200 px-4 py-3.5 flex items-center gap-3"
                >
                  <span className="w-9 h-9 shrink-0 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{item.k}</dt>
                    <dd className="mt-0.5 font-label text-sm text-foreground-900 truncate">{item.v}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <div className="lg:col-span-8 flex flex-col gap-8">
                <article className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-7">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                      <i className="ri-pulse-line" />
                    </span>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                      热度走势
                    </h2>
                  </div>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    以该事件自身峰值为 100 折算的相对热度序列，横轴按首爆时间对齐。
                    虚线标记出现峰值的时刻，便于和发酵时间线对照阅读。
                  </p>
                  <div className="mt-5">
                    <HeatChart data={event.heatSeries} />
                  </div>
                </article>

                <article className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-7">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                      <i className="ri-timeline-view" />
                    </span>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                      发酵时间线
                    </h2>
                  </div>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    从 T+0 首爆开始，按时间顺序记录每个扩散节点的素材形式、渠道变化与话题走向。
                  </p>
                  <div className="mt-7">
                    <Timeline nodes={event.timeline} />
                  </div>
                </article>

                <article className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-7">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-secondary-600">
                      <i className="ri-article-line" />
                    </span>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                      传播解读
                    </h2>
                  </div>
                  <div className="mt-4 flex flex-col gap-4">
                    {event.analysis.map((paragraph, index) => (
                      <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </article>
              </div>

              <aside className="lg:col-span-4 flex flex-col gap-6">
                <div className="rounded-2xl bg-secondary-900 text-background-50 p-5 md:p-6">
                  <h2 className="font-heading text-base font-bold">关键指标</h2>
                  <div className="mt-5 grid grid-cols-2 gap-5">
                    <div>
                      <p className="text-xs text-secondary-300">热度值</p>
                      <p className="mt-1 font-heading text-xl font-bold text-accent-300">
                        {formatHeat(event.heat)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-300">话题量</p>
                      <p className="mt-1 font-heading text-xl font-bold">{event.topicCount}万</p>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-300">转发量</p>
                      <p className="mt-1 font-heading text-xl font-bold">{event.repost}万</p>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-300">综合排名</p>
                      <p className="mt-1 font-heading text-xl font-bold">No.{event.rank}</p>
                    </div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-secondary-700 flex items-center justify-between">
                    <span className="text-xs text-secondary-300">当前变化</span>
                    <span className="font-label text-sm text-accent-300">
                      {event.trendRate > 0 ? "+" : ""}
                      {event.trendRate}%
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-secondary-600">
                      <i className="ri-node-tree" />
                    </span>
                    扩散渠道占比
                  </h2>
                  <ul className="mt-5 space-y-4">
                    {event.channels.map((channel) => (
                      <li key={channel.name}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-foreground-700">{channel.name}</span>
                          <span className="font-label text-foreground-900">{channel.value}%</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              channel.value === maxChannel
                                ? "bg-gradient-to-r from-primary-400 to-primary-600"
                                : "bg-gradient-to-r from-secondary-400 to-secondary-600"
                            }`}
                            style={{ width: `${channel.value}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950">相关事件推荐</h2>
                  <ul className="mt-4 flex flex-col divide-y divide-background-200">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/event/${item.id}`}
                          className="group flex items-start gap-3 py-3.5 first:pt-0 last:pb-0"
                        >
                          <span className="w-16 h-12 shrink-0 rounded-lg overflow-hidden">
                            <img
                              src={item.cover}
                              alt={`${item.title}传播路径示意图`}
                              title={`${item.title} · 发酵与扩散追踪`}
                              className="w-full h-full object-cover object-top"
                            />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                              {item.title}
                            </span>
                            <span className="mt-1 block text-xs text-foreground-500">
                              {item.category} · 话题 {item.topicCount}万
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-t border-background-200 py-12 md:py-16">
          <div className="w-full px-4 md:px-8">
            <div className="rounded-2xl bg-background-50 border border-background-200 p-6 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  想看更多同类型事件的传播结构？
                </h2>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-xl">
                  事件追踪库里有更多处在不同发酵阶段的话题，可以按分类筛选，也可以直接对比热度曲线。
                </p>
              </div>
              <Link
                to="/events"
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label whitespace-nowrap transition-colors shrink-0"
              >
                前往事件追踪库
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        <SiteFooter />
        <BackToTop />
      </div>
    </>
  );
}